import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import styled from 'styled-components';

// Material-UI
import Autocomplete from '@material-ui/lab/Autocomplete';
import Popper from '@material-ui/core/Popper';
import CircularProgress from '@material-ui/core/CircularProgress';

import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';

import { Icons, MUI, Unicons, Typography } from '@styles';

const MuiPopper = (props) => {
  return (
    <Popper
      {...props}
      placement="bottom-start"
      modifiers={{ computeStyle: { gpuAcceleration: false } }}
    />
  );
};

const MuiAutocomplete = (props) => {
  const {
    label,
    searchList,
    searchBy,
    value,
    error,
    helperText,
    icon,
    showIcon = true,
    onChange,
    renderOption,
    ...restProps
  } = props;

  const options = Array.isArray(searchList) ? searchList : [];
  const hasOptions = options.length > 0;
  const [isLoading, setIsLoading] = useState(!hasOptions);

  useEffect(() => {
    setIsLoading(!hasOptions);
  }, [hasOptions]);

  const Loading = () => {
    return (
      <BoxLoading>
        <SpanText>
          {`${i18n.t('GenericMessages.loading')}...`}
        </SpanText>
        <BoxCircularSpinner>
          <StyledCircularProgress size={20} thickness={4} />
        </BoxCircularSpinner>
      </BoxLoading>
    );
  };

  const NotFound = () => {
    return (
      <SpanText>
        {`${i18n.t('GenericMessages.noResults')}...`}
      </SpanText>
    );
  };

  const getOptionLabel = (option) => {
    if (!option) {
      return '';
    }

    if (searchBy && typeof option === 'object') {
      return option[searchBy] || '';
    }

    return String(option);
  };

  return (
    <Autocomplete
      {...restProps}
      // debug={true}
      loading={isLoading}
      clearOnEscape={true}
      clearText={i18n.t('GenericMessages.clear')}
      closeText={i18n.t('DialogConfirm.close')}
      openText={i18n.t('GenericMessages.open')}
      loadingText={<Loading />}
      noOptionsText={<NotFound />}
      options={options}
      getOptionLabel={getOptionLabel}
      value={value}
      onChange={(event, newValue) => {
        if (typeof onChange === 'function') {
          onChange(newValue);
        }
      }}
      popupIcon={<Icons.ArrowDown />}
      PopperComponent={StyledPopper}
      renderInput={(params) =>
        showIcon === false ? (
          <MUI.TextField
            {...params}
            {...restProps}
            label={label}
            error={error}
            helperText={helperText}
            InputProps={{ ...params.InputProps }}
          />
        ) : (
          <MUI.TextFieldIcon
            {...params}
            {...restProps}
            label={label}
            error={error}
            helperText={helperText}
            InputProps={{ ...params.InputProps, startAdornment: icon ? icon : <Unicons.Search size="20" /> }}
          />
        )}
      renderOption={renderOption ? renderOption : (option, { inputValue }) => {
        const labelText = getOptionLabel(option);
        const matches = match(labelText, inputValue || '');
        const parts = parse(labelText, matches);

        return (
          <Typography.Text overflow={1}>
            {parts.map((part, index) => (
              <SpanText key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                {part.text}
              </SpanText>
            ))}
          </Typography.Text>
        );
      }}
    />
  );
};

MuiAutocomplete.propTypes = {
  // The label content.
  label: PropTypes.node,
  // Array of options.
  searchList: PropTypes.array.isRequired,
  // Used to determine the string value for a given option.
  searchBy: PropTypes.string,
  // The value of the autocomplete.
  value: PropTypes.oneOfType([PropTypes.any, PropTypes.array]),
  // The error bool.
  error: PropTypes.bool,
  // The helper text content.
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.node]),
  // The icon in the input.
  icon: PropTypes.any,
  // Boolean to show the icon in the input.
  showIcon: PropTypes.bool,
  // Callback fired when the value changes.
  onChange: PropTypes.func,
  // Render the selected value.
  renderOption: PropTypes.func
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const SpanText = styled.span`
  font-family: 'Bryant Pro Regular';
  font-size: ${(props) => props.theme.typography.bodyText};
  font-weight: normal;
  line-height: normal;
  letter-spacing: 0.0175em;
  color: ${(props) => props.theme.colors.greyDark};
`;

const BoxLoading = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
`;

const BoxCircularSpinner = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: flex-end;
`;

// Popper 
const StyledPopper = styled(MuiPopper)`
  .MuiPaper-root {
    margin: 8px 0;
    box-shadow: ${(props) => props.theme.shadows.depth2};
  }

  .MuiAutocomplete-listbox {
    background-color: ${(props) => props.theme.colors.white};
    backface-visibility: hidden;
    scrollbar-width: thin !important;
  }

  .MuiAutocomplete-option {
    min-height: 40px;
    padding: 0px 16px;
  }

  .MuiAutocomplete-option[aria-selected="true"] {
    background-color: ${(props) => props.theme.colors.greyLight};
  }
`;

// Autocomplete
export const StyledAutocomplete = styled(MuiAutocomplete)`
  .MuiAutocomplete-inputRoot .MuiAutocomplete-input {
    padding: 0px !important;
  }
  
  .MuiAutocomplete-clearIndicator {
    padding: 2px;
    visibility: ${(props) => (props.value === '' || props.value === null) ? 'hidden' : 'visible'};
  }
`;

// Circular Progress
const StyledCircularProgress = styled(CircularProgress)`
  &.MuiCircularProgress-root {
    color: ${(props) => props.theme.colors.blueMedium};
  }
`;